import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  PROVIDER_LABELS, authErrorMessage, clearPendingLink, getPendingLink, isConfigured,
  sendReset, signIn, signInWithApple, signInWithEmail, signUpWithEmail,
} from '../firebase'
import { AppleIcon, GoogleIcon } from './icons'

type Mode = 'signin' | 'signup' | 'reset'

const COPY: Record<Mode, { title: string; action: string; alt: string; altLabel: string }> = {
  signin: { title: 'Sign in to Indigo Notes', action: 'Sign in', alt: 'signup', altLabel: 'Create an account' },
  signup: { title: 'Create your account', action: 'Create account', alt: 'signin', altLabel: 'I already have an account' },
  reset: { title: 'Reset your password', action: 'Send reset link', alt: 'signin', altLabel: 'Back to sign in' },
}

export function AuthDialog({ onClose, initialMode = 'signin' }: {
  onClose: () => void
  /** Which view to open on. Creating an account is a different intent from
   *  signing in, so a "Sign up" button should not land on the sign-in form. */
  initialMode?: Mode
}) {
  const [mode, setMode] = useState<Mode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [link, setLink] = useState(getPendingLink())
  const emailRef = useRef<HTMLInputElement>(null)

  useEffect(() => { emailRef.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null)
    try { await fn() } catch (e) {
      setError(authErrorMessage(e))
      // A conflicting provider parks its credential; show the link prompt.
      const parked = getPendingLink()
      if (parked) { setLink(parked); setEmail(parked.email) }
    } finally { setBusy(false) }
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    void run(async () => {
      if (mode === 'reset') { await sendReset(email); setSent(true); return }
      if (mode === 'signup') return signUpWithEmail(email, password)
      return signInWithEmail(email, password)
    })
  }

  const copy = COPY[mode]

  return createPortal(
    <div className="auth-scrim" onMouseDown={onClose}>
      <div className="auth" role="dialog" aria-label={copy.title} onMouseDown={(e) => e.stopPropagation()}>
        <img className="auth-logo" src="./favicon.png" alt="" width="44" height="44" />
        <h2>{link ? 'Link your sign-in methods' : copy.title}</h2>

        {link && (
          <p className="auth-link-note">
            <strong>{link.email}</strong> already has an account
            {link.methods.length
              ? <> with {link.methods.map((m) => PROVIDER_LABELS[m] ?? m).join(' and ')}</>
              : null}.
            {' '}Sign in the way you did before and we’ll attach this method to the
            same notes — you’ll keep one account either way.
          </p>
        )}

        {mode !== 'reset' && (
          <>
            <div className="auth-providers">
              <button className="auth-provider" disabled={busy || !isConfigured}
                      onClick={() => void run(signIn)}>
                <GoogleIcon />Continue with Google
              </button>
              <button className="auth-provider" disabled={busy || !isConfigured}
                      onClick={() => void run(signInWithApple)}>
                <AppleIcon />Continue with Apple
              </button>
            </div>
            <div className="auth-or"><span>or</span></div>
          </>
        )}

        <form className="auth-form" onSubmit={submit}>
          <label>
            Email
            <input ref={emailRef} type="email" autoComplete="email" required
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {mode !== 'reset' && (
            <label>
              Password
              <input type="password" required minLength={6}
                     autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                     value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
          )}
          <button className="auth-submit" type="submit" disabled={busy || !isConfigured}>
            {busy ? 'Working…' : copy.action}
          </button>
        </form>

        {sent && <p className="auth-ok">Check your inbox for a reset link.</p>}
        {error && <p className="auth-error">{error}</p>}
        {!isConfigured && <p className="auth-error">This deployment is missing its Firebase configuration.</p>}

        <div className="auth-alt">
          {link ? (
            <button onClick={() => { clearPendingLink(); setLink(null); setError(null) }}>
              Cancel linking
            </button>
          ) : (
            <button onClick={() => { setMode(copy.alt as Mode); setError(null); setSent(false) }}>
              {copy.altLabel}
            </button>
          )}
          {mode === 'signin' && (
            <button onClick={() => { setMode('reset'); setError(null) }}>Forgot password?</button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
